import { supabase } from '../supabase';

/**
 * Create a notification in the database
 */
export async function createNotification(userId, type, title, message, data = {}) {
    try {
        const { data: notification, error } = await supabase
            .from('notifications')
            .insert([{
                user_id: userId,
                type,
                title,
                message,
                data,
                read: false
            }])
            .select()
            .single();

        if (error) {
            throw error;
        }

        return notification;
    } catch (error) {
        throw error;
    }
}

/**
 * Get unread notifications for a user
 */
export async function getUnreadNotifications(userId, limit = 50) {
    try {
        const { data, error } = await supabase
            .from('notifications')
            .select('*')
            .eq('user_id', userId)
            .eq('read', false)
            .order('created_at', { ascending: false })
            .limit(limit);

        if (error) throw error;
        return data || [];
    } catch (error) {
        return [];
    }
}

/**
 * Mark notification as read
 */
export async function markNotificationAsRead(notificationId, userId) {
    try {
        const { error } = await supabase
            .from('notifications')
            .update({ read: true })
            .eq('id', notificationId)
            .eq('user_id', userId);

        if (error) throw error;
        return true;
    } catch (error) {
        return false;
    }
}

/**
 * Mark all notifications as read for a user
 */
export async function markAllNotificationsAsRead(userId) {
    try {
        const { error } = await supabase
            .from('notifications')
            .update({ read: true })
            .eq('user_id', userId)
            .eq('read', false);

        if (error) throw error;
        return true;
    } catch (error) {
        return false;
    }
}

/**
 * Delete a notification
 */
export async function deleteNotification(notificationId, userId) {
    try {
        const { error } = await supabase
            .from('notifications')
            .delete()
            .eq('id', notificationId)
            .eq('user_id', userId);

        if (error) throw error;
        return true;
    } catch (error) {
        return false;
    }
}

/**
 * Create notification when a message is sent
 * This should be called after sending a message
 */
export async function createMessageNotification(message, conversation, senderProfile, recipientId) {
    try {
        const senderName = senderProfile?.companyname || 
            `${senderProfile?.firstname || ''} ${senderProfile?.lastname || ''}`.trim() || 
            'Someone';

        const notification = await createNotification(
            recipientId,
            'message',
            'New Message',
            senderName,
            {
                conversation_id: conversation.id,
                message_id: message.id,
                sender_id: message.sender_id,
                sender_name: senderName,
                content: message.content
            }
        );
        
        return notification;
    } catch (error) {
        throw error;
    }
}

/**
 * Create notification when an application is submitted
 */
export async function createApplicationNotification(application, job, candidateProfile, employerId) {
    try {
        const candidateName = candidateProfile?.companyname || 
            `${candidateProfile?.firstname || ''} ${candidateProfile?.lastname || ''}`.trim() || 
            'A candidate';

        await createNotification(
            employerId,
            'application',
            'New Application',
            `${candidateName} applied to ${job.title}`,
            {
                application_id: application.id,
                candidate_id: application.user_id,
                job_id: application.job_listing_id,
                job_title: job.title
            }
        );
    } catch (error) {
        // Silent fail for application notifications
    }
}

